import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CONTACT_OPTIONS_LABEL,
  FIREBASE_EVENTS,
  MODAL_DRAWER_KEY,
  RESEND_TOKEN_TIME,
  STATUS_CODE,
  STORE_STAGES,
  TOKEN_LENGTH,
} from 'app-constants'
import { CountdownTime, OtpInput } from 'components'
import { productService, tokenService } from 'services'
import { clearDrawerModal, setDrawerAlert, setDrawerModal, setStage } from 'slices/drawer'
import { useFirebaseEvent } from 'hooks'
import { STATICS } from 'statics'
import { getMaskedContact } from 'utils'
import { ActionContainer } from '../action-container'
import {
  CONFIRM_SUBSCRIPTION_STEPS,
  DEFAULT_ERROR_GENERATE_TOKEN_MESSAGE,
  EXPIRED_TOKEN_MESSAGE,
  INCORRECT_TOKEN_MESSAGE,
} from '../../constants'
import './style.scss'

export function TokenValidation({
  isLoading,
  document,
  setIsLoading,
  setCurrentStep,
  contactToken,
  contactTokenType,
  cart,
}) {
  const [code, setCode] = useState('')
  const [canSendCode, setCanSendCode] = useState(false)
  const [error, setError] = useState(null)
  const { sendEvent } = useFirebaseEvent()
  const { generateToken } = tokenService()
  const { confirmSubscription } = productService()
  const { id: contractId } = useSelector(({ contract }) => contract.selected)
  const dispatch = useDispatch()

  useEffect(() => {
    setError('')
  }, [code])

  function handleBackClick() {
    setCurrentStep(CONFIRM_SUBSCRIPTION_STEPS.SELECT_CONTACT)
  }

  async function handleConfirmClick() {
    try {
      setIsLoading(true)
      sendEvent(FIREBASE_EVENTS.SOFTBUNDLE_PURCHASE)

      const productsIds = cart.map(item => item.id)
      await confirmSubscription(contractId, productsIds, code)

      sendEvent(FIREBASE_EVENTS.SOFTBUNDLE_PURCHASE_SUCCESS)
      dispatch(clearDrawerModal())
      dispatch(setStage(STORE_STAGES.SUCCESS))
    } catch (err) {
      console.error(err)
      if (err?.response?.status === STATUS_CODE.INVALID_TOKEN) {
        setError(INCORRECT_TOKEN_MESSAGE)
      } else {
        handleErrorModal()
      }
    } finally {
      setIsLoading(false)
    }
  }

  function handleErrorModal() {
    dispatch(
      setDrawerModal({
        key: MODAL_DRAWER_KEY.STORE_SUBSCRIPTION_ERROR,
      })
    )
  }

  async function handleSendAgainClick() {
    if (!canSendCode) return null
    setIsLoading(true)
    try {
      await generateToken(document, contactToken, contactTokenType)
      setCanSendCode(false)
    } catch (e) {
      const errorMessage = error?.response?.data?.message || DEFAULT_ERROR_GENERATE_TOKEN_MESSAGE
      dispatch(setDrawerAlert({ severity: 'error', title: errorMessage }))
    } finally {
      setIsLoading(false)
    }
  }

  function handleExpiredCode() {
    setCanSendCode(true)
  }

  function renderContact() {
    const contactType = CONTACT_OPTIONS_LABEL[contactTokenType]
    const maskedContact = getMaskedContact(contactToken)
    return (
      <strong>
        {contactType} ({maskedContact})
      </strong>
    )
  }

  function renderInfo() {
    if (error) return <p className='info -error'>{error}</p>

    if (canSendCode) return <p className='info -expired'>{EXPIRED_TOKEN_MESSAGE}</p>

    return <p className='info'>Digite o código de 6 dígitos recebido</p>
  }

  function renderCountdown() {
    if (canSendCode) return null

    return (
      <>
        em{' '}
        <CountdownTime
          onFinishCounter={handleExpiredCode}
          time={RESEND_TOKEN_TIME}
          canSendCode={canSendCode}
        />
      </>
    )
  }

  function renderSendAgainButton() {
    return (
      <button
        className='send-again-button'
        type='button'
        onClick={handleSendAgainClick}
        disabled={!canSendCode}
      >
        {!canSendCode && <STATICS.CLOCK className='icon' />}
        <span>Reenviar código {renderCountdown()}</span>
      </button>
    )
  }

  return (
    <div className='token-validation-container'>
      <h3 className='title'>Validação de segurança</h3>
      <p className='description'>
        Enviamos um código de 6 dígitos para seu {renderContact()} cadastrado.
      </p>
      <hr className='divider' />
      <div className='token-container'>
        {renderInfo()}
        <OtpInput maxLength={TOKEN_LENGTH} setCode={setCode} />
        {renderSendAgainButton()}
      </div>
      <ActionContainer
        labelConfirm='Confirmar código'
        onBack={handleBackClick}
        onConfirm={handleConfirmClick}
        isLoading={isLoading}
        disabled={canSendCode || TOKEN_LENGTH !== code.length}
      />
    </div>
  )
}
